import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import styles from "../styles.module.scss";
import { CheckIcon, ReviewDocIcon, UploadIcon } from "../gateIcons";
import {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
  ORGANIZATION_DOCUMENT_SLOTS,
} from "../organizationDocuments.constants";
import ActivationTaskModal from "./ActivationTaskModal";
import { IDocumentsDraft } from "./types";

interface Props {
  visible: boolean;
  /** Previously picked files, when the user reopens the task to edit it. */
  initialValue: IDocumentsDraft | null;
  onSave: (value: IDocumentsDraft) => void;
  onClose: () => void;
}

/**
 * Activation task: pick the required documents. Nothing is uploaded here — the files are held
 * until the whole request is submitted (see OrganizationActivation).
 */
const DocumentsModal: React.FC<Props> = ({
  visible,
  initialValue,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<IDocumentsDraft>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Every opening starts from what was last saved, so cancelling discards edits.
  useEffect(() => {
    if (!visible) return;
    setFiles(initialValue ?? {});
    setFieldErrors({});
  }, [visible, initialValue]);

  const onPick = (type: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file after an error still fires onChange.
    e.target.value = "";
    if (!file) return;

    if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({
        ...prev,
        [type]: t("lbl_org_docs_invalid_type"),
      }));
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setFieldErrors((prev) => ({
        ...prev,
        [type]: t("lbl_org_docs_too_large"),
      }));
      return;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  // Every document is mandatory.
  const canSave = ORGANIZATION_DOCUMENT_SLOTS.every(
    ({ type }) => !!files[type]
  );

  return (
    <ActivationTaskModal
      visible={visible}
      onClose={onClose}
      title={t("lbl_org_act_docs_title")}
      subtitle={t("lbl_org_act_docs_subtitle") as string}
      actions={
        <>
          <PrimaryButton
            type="button"
            disabled={!canSave}
            onClick={() => canSave && onSave(files)}>
            {t("lbl_org_act_save")}
          </PrimaryButton>
          <PrimaryButton
            type="button"
            buttonType={PrimaryButtonType.PRIMARY_LIGHT}
            onClick={onClose}>
            {t("lbl_org_act_cancel")}
          </PrimaryButton>
        </>
      }>
      <div className={styles.docList}>
        {ORGANIZATION_DOCUMENT_SLOTS.map(({ type, labelKey }) => {
          const file = files[type];
          const fieldError = fieldErrors[type];
          return (
            <label
              key={type}
              className={classNames(styles.docUpload, {
                [styles.hasFile]: !!file,
                [styles.hasError]: !!fieldError,
              })}>
              <input
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES.join(",")}
                hidden
                onChange={onPick(type)}
              />
              <span className={styles.duIcon}>
                <ReviewDocIcon />
              </span>
              <span className={styles.duTxt}>
                <b>{t(labelKey)}</b>
                {fieldError ? (
                  <span className={styles.duError}>{fieldError}</span>
                ) : file ? (
                  // dir="auto" so a Latin file name ellipsizes at its own end.
                  <span className={styles.duFile} dir="auto">
                    {file.name}
                  </span>
                ) : (
                  <span className={styles.duHint}>
                    {t("lbl_org_docs_hint")}
                  </span>
                )}
              </span>
              <span className={styles.duCta}>
                <UploadIcon />
                {file ? t("lbl_org_docs_change") : t("lbl_org_docs_upload")}
              </span>
              {file && (
                <span className={styles.duDone}>
                  <CheckIcon />
                </span>
              )}
            </label>
          );
        })}
      </div>
    </ActivationTaskModal>
  );
};

export default DocumentsModal;
